// src/pages/tender/VendorTenderStatus.js
import { useEffect, useRef, useState } from "react";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { collection, query, where, onSnapshot, getDoc, doc } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import { Link, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import dayjs from "dayjs";

/* ------------------ small UI helpers ------------------ */
const cx = (...a) => a.filter(Boolean).join(" ");
const INR = (n) => `INR ${Number(n || 0).toLocaleString("en-IN")}`;
const toAscii = (s = "") =>
  String(s)
    .replace(/₹/g, "INR ")
    .replace(/[–—]/g, "-")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\u00A0/g, " ");

const palette = {
  banner1: [13, 148, 136],
  banner2: [2, 132, 199],
  text: [22, 22, 22],
  sub: [105, 117, 134],
  line: [210, 218, 226],
  zebra: [248, 250, 252],
  amber: [255, 251, 235],
  white: [255, 255, 255],
  light: [246, 248, 250],
  mint: [236, 253, 245],
  sky: [239, 246, 255],
};

/* ------------------ time helpers ------------------ */
const useTicker = (ms = 1000) => {
  const [t, setT] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setT(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return t;
};

const remainingVerbose = (targetISO, now = Date.now()) => {
  if (!targetISO) return "—";
  const diff = new Date(targetISO).getTime() - now;
  if (diff <= 0) return "00:00:00";
  const d = Math.floor(diff / (24 * 3600e3));
  const h = Math.floor((diff % (24 * 3600e3)) / 3600e3);
  const m = Math.floor((diff % 3600e3) / 60e3);
  const s = Math.floor((diff % 60e3) / 1e3);
  if (d > 0) {
    return `${d}d ${String(h).padStart(2, "0")}:${String(m).padStart(
      2,
      "0"
    )}:${String(s).padStart(2, "0")}`;
  }
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(
    s
  ).padStart(2, "0")}`;
};

const pickOpenAt = (t) => t?.autoOpenAt || t?.endAt || t?.closeDate || null;

/* ------------------ helpers for updated rates ------------------ */
const getAppliedAt = (a) =>
  a?.submittedAt?.toDate?.() ||
  a?.appliedAt?.toDate?.() ||
  a?.createdAt?.toDate?.() ||
  new Date();

const getTenderSortTime = (t) => {
  return (
    new Date(
      t?.autoOpenAt || t?.endAt || t?.startAt || t?.closeDate || t?.openDate || 0
    ).getTime() || 0
  );
};

const getEffectiveAmount = (a, tenderBase = 0) => {
  if (a?.effectiveAmount !== undefined && a?.effectiveAmount !== null) {
    return Number(a.effectiveAmount || 0);
  }

  if (a?.quotedAmount !== undefined && a?.quotedAmount !== null) {
    return Number(a.quotedAmount || 0);
  }

  const delta = Number(a?.deltaAmount || 0);
  const selectType = String(a?.selectType || "").toUpperCase();

  if (selectType === "ABOVE") return Number(tenderBase || 0) + delta;
  if (selectType === "BELOW") return Number(tenderBase || 0) - delta;

  return Number(tenderBase || 0);
};

const getPercentValue = (a, tenderBase = 0, effectiveAmount = 0) => {
  if (
    a?.percentAboveBelow !== undefined &&
    a?.percentAboveBelow !== null &&
    a?.effectiveAmount == null
  ) {
    return Number(a.percentAboveBelow || 0);
  }
  if (!Number(tenderBase || 0)) return 0;
  return (
    ((Number(effectiveAmount || 0) - Number(tenderBase || 0)) /
      Number(tenderBase || 1)) *
    100
  );
};

const compactCity = (s = "") => {
  const v = String(s || "").trim();
  if (!v) return "—";
  return v;
};

export default function VendorTenderStatus() {
  const auth = getAuth();
  const nav = useNavigate();

  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const [dataMap, setDataMap] = useState({});
  const unsubRefs = useRef([]);

  const sessionTick = useTicker(1000);
  const SESSION_MIN = 7;
  const [deadline] = useState(() => Date.now() + SESSION_MIN * 60 * 1000);

  useEffect(() => {
    if (Date.now() >= deadline && user) {
      signOut(auth).finally(() => nav("/tender?tab=login"));
    }
  }, [sessionTick]); // eslint-disable-line

  useEffect(() => {
    const off = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      try {
        if (u?.uid) localStorage.setItem("vendorUid", u.uid);
      } catch {}
      setReady(true);
    });
    return () => off();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (ready && !user) nav("/tender?tab=login");
  }, [ready, user, nav]);

  useEffect(() => {
    unsubRefs.current.forEach((fn) => fn && fn());
    unsubRefs.current = [];
    if (!user) return;

    const qMine = query(
      collection(db, "tender_applications"),
      where("vendorUid", "==", user.uid)
    );

    const unsubMine = onSnapshot(qMine, async (snap) => {
      const mine = [];
      snap.forEach((d) => mine.push({ id: d.id, ...d.data() }));
      const tenderIds = Array.from(new Set(mine.map((m) => m.tenderId))).filter(Boolean);

      const seedMap = {};
      const inner = [];

      for (const tid of tenderIds) {
        const td = await getDoc(doc(db, "tenders", tid));
        const tender = td.exists() ? { id: td.id, ...td.data() } : null;
        const openAt = pickOpenAt(tender);

        const qAll = query(
          collection(db, "tender_applications"),
          where("tenderId", "==", tid)
        );

        const unsubAll = onSnapshot(qAll, (asnap) => {
          const all = [];
          asnap.forEach((ad) => all.push({ id: ad.id, ...ad.data() }));
          setDataMap((prev) => ({ ...prev, [tid]: { tender, apps: all, openAt } }));
        });

        inner.push(unsubAll);
        seedMap[tid] = { tender, apps: [], openAt };
      }

      setDataMap((prev) => ({ ...seedMap, ...prev }));
      unsubRefs.current.push(...inner);
    });

    unsubRefs.current.push(unsubMine);

    return () => {
      unsubRefs.current.forEach((fn) => fn && fn());
      unsubRefs.current = [];
    };
  }, [user]);

  const tick = useTicker(1000);
  const pdfTriggeredRef = useRef({});

  /* ------------------ PDF helpers ------------------ */
  const textLines = (pdf, txt, maxW) =>
    pdf.splitTextToSize(toAscii(txt || "—"), maxW);

  const textRight = (pdf, txt, xRight, y) => {
    const w = pdf.getTextWidth(String(txt || ""));
    pdf.text(String(txt || ""), xRight - w, y);
  };

  const drawHeader = (pdf) => {
    const W = pdf.internal.pageSize.getWidth();
    pdf.setFillColor(...palette.banner1);
    pdf.rect(0, 0, W * 0.52, 80, "F");
    pdf.setFillColor(...palette.banner2);
    pdf.rect(W * 0.52, 0, W * 0.48, 80, "F");

    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    pdf.text("Vendor Tender Results", 40, 34);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    pdf.text(`Downloaded: ${dayjs().format("DD MMM YYYY, hh:mm A")}`, 40, 56);

    const uid =
      (typeof window !== "undefined" && localStorage.getItem("vendorUid")) || "";
    if (uid) {
      textRight(pdf, `Vendor UID: ${uid}`, W - 40, 56);
    }

    pdf.setTextColor(...palette.text);
  };

  const ensureSpace = (pdf, y, need) => {
    const H = pdf.internal.pageSize.getHeight();
    if (y + need <= H - 36) return y;
    pdf.addPage();
    drawHeader(pdf);
    return 96;
  };

  const sectionTitle = (pdf, title, y) => {
    const W = pdf.internal.pageSize.getWidth();
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.text(toAscii(title), 32, y);
    y += 8;
    pdf.setDrawColor(...palette.line);
    pdf.setLineWidth(1);
    pdf.line(32, y, W - 32, y);
    y += 14;
    return y;
  };

  const drawMiniCardDynamic = (pdf, x, y, w, label, value, fill) => {
    const labelLines = textLines(pdf, label, w - 16);
    const valueLines = textLines(pdf, value, w - 16);

    const labelH = Math.max(10, labelLines.length * 9);
    const valueH = Math.max(12, valueLines.length * 10);
    const h = 8 + labelH + 4 + valueH + 8;

    pdf.setDrawColor(...palette.line);
    pdf.setFillColor(...fill);
    pdf.rect(x, y, w, h, "FD");

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.8);
    pdf.setTextColor(...palette.sub);
    pdf.text(labelLines, x + 8, y + 12);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9.6);
    pdf.setTextColor(...palette.text);
    pdf.text(valueLines, x + 8, y + 12 + labelH + 4);

    return h;
  };
  const drawTablePaged = (pdf, y, cols, headers, rows) => {
  const H = pdf.internal.pageSize.getHeight();
  const startX = 32;
  const padX = 4;
  const totalW = cols.reduce((a, b) => a + b, 0);

  const drawHeaderRow = () => {
    y = ensureSpace(pdf, y, 24);

    pdf.setDrawColor(0);
    pdf.rect(startX, y, totalW, 22);

    let x = startX;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);

    headers.forEach((h, i) => {
      pdf.text(h, x + padX, y + 14);
      x += cols[i];
      if (i < headers.length - 1) pdf.line(x, y, x, y + 22);
    });

    y += 22;
    return y;
  };

  const drawRow = (row, idx) => {
    const blocks = row.map((cell, colIdx) =>
      pdf.splitTextToSize(String(cell || ""), cols[colIdx] - 6)
    );

    const rowH = Math.max(20, ...blocks.map((b) => b.length * 12 + 6));

    if (y + rowH > H - 40) {
      pdf.addPage();
      drawHeader(pdf);
      y = 100;
      y = drawHeaderRow();
    }

    pdf.rect(startX, y, totalW, rowH);

    let x = startX;
    for (let i = 0; i < cols.length - 1; i++) {
      x += cols[i];
      pdf.line(x, y, x, y + rowH);
    }

    let tx = startX;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);

    blocks.forEach((lines, i) => {
      lines.forEach((ln, li) => {
        pdf.text(ln, tx + padX, y + 13 + li * 12);
      });
      tx += cols[i];
    });

    y += rowH;
    return y;
  };

  y = drawHeaderRow();
  rows.forEach((r, i) => {
    y = drawRow(r, i);
  });

  return y;
};

  // ✅ FINAL fully dynamic card
  const drawOpenTenderBox = (pdf, block, y) => {
  const W = pdf.internal.pageSize.getWidth();
  const leftX = 32;
  const rightX = W - 32;
  const cardW = rightX - leftX;

  const pad = 12;
  const gap = 8;

  // ===== TITLE =====
  const titleMaxW = cardW - pad * 2 - 20;
  const titleLines = textLines(pdf, block.tender?.title || "Tender", titleMaxW);

  const lineHeight = 14;
  const titleH = Math.max(20, titleLines.length * lineHeight);

  // ===== VALUES =====
  const companyVal = block.tender?.ownerCompany || "—";
  const cityVal = block.tender?.city || "—";

  const openedVal =
    block.tender?.autoOpenAt
      ? dayjs(block.tender.autoOpenAt).format("DD/MM/YYYY, hh:mm A")
      : block.tender?.endAt
      ? dayjs(block.tender.endAt).format("DD/MM/YYYY, hh:mm A")
      : "—";

  const estimatedVal = INR(block.tender?.baseAmount || block.tender?.amount || 0);

  // ===== COLUMN WIDTH =====
  const colW = (cardW - pad * 2 - gap * 3) / 4;

  // ===== DYNAMIC HEIGHT =====
  const calcHeight = (label, value) => {
    const l = textLines(pdf, label, colW - 16);
    const v = textLines(pdf, value, colW - 16);
    return 10 + l.length * 9 + v.length * 10;
  };

  const h1 = calcHeight("Company", companyVal);
  const h2 = calcHeight("City", cityVal);
  const h3 = calcHeight("Opened", openedVal);
  const h4 = calcHeight("Estimated", estimatedVal);

  const cardsMaxH = Math.max(h1, h2, h3, h4);

  // ===== FINAL HEIGHT =====
  const totalH = 24 + titleH + 26 + cardsMaxH + 20;

  y = ensureSpace(pdf, y, totalH + 10);

  // ===== BOX =====
  pdf.setDrawColor(0);
  pdf.setFillColor(...palette.white);
  pdf.rect(leftX, y, cardW, totalH, "FD");

  // ===== TITLE =====
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10.5);
  pdf.setTextColor(...palette.text);
  pdf.text(titleLines, leftX + pad, y + 18, {
  maxWidth: titleMaxW
});

  // ===== CARD Y (IMPORTANT FIX) =====
  const cardsY = y + 20 + titleH;

  const drawCard = (x, label, value, fill) => {
    const l = textLines(pdf, label, colW - 16);
    const v = textLines(pdf, value, colW - 16);

    pdf.setDrawColor(...palette.line);
    pdf.setFillColor(...fill);
    pdf.rect(x, cardsY, colW, cardsMaxH, "FD");

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.5);
    pdf.setTextColor(...palette.sub);
    pdf.text(l, x + 6, cardsY + 12);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9.5);
    pdf.setTextColor(...palette.text);
    pdf.text(v, x + 6, cardsY + 12 + l.length * 9);
  };

  const c1 = leftX + pad;
  const c2 = c1 + colW + gap;
  const c3 = c2 + colW + gap;
  const c4 = c3 + colW + gap;

  drawCard(c1, "Company", companyVal, palette.light);
  drawCard(c2, "City", cityVal, palette.sky);
  drawCard(c3, "Opened", openedVal, palette.light);
  drawCard(c4, "Estimated", estimatedVal, palette.mint);

  return y + totalH + 10;
};
  /* ------------------ PDF generator ------------------ */
  const downloadPDF = () => {
    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const W = pdf.internal.pageSize.getWidth();
    const H = pdf.internal.pageSize.getHeight();

    drawHeader(pdf);
    let y = 96;

    const ids = Object.keys(dataMap).sort((a, b) => {
      const ta = dataMap[a]?.tender;
      const tb = dataMap[b]?.tender;
      return getTenderSortTime(tb) - getTenderSortTime(ta);
    });

    const openedBlocks = [];
    const lockedBlocks = [];

    ids.forEach((tid) => {
      const { tender, apps, openAt } = dataMap[tid] || {};
      const opened = !openAt || Date.now() >= new Date(openAt).getTime();
      const block = { tender, apps: apps || [], openAt };
      if (opened) openedBlocks.push(block);
      else lockedBlocks.push(block);
    });

    y = sectionTitle(pdf, "OPEN TENDERS", y);

    if (!openedBlocks.length) {
      pdf.setTextColor(...palette.sub);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      pdf.text("No open tenders to print.", 32, y);
      pdf.setTextColor(...palette.text);
      y += 18;
    } else {
      openedBlocks.forEach((b, cardIdx) => {
        y = drawOpenTenderBox(pdf, b, y);

        const tenderBase = Number(b.tender?.baseAmount || b.tender?.amount || 0);

        const ranked = (b.apps || [])
          .map((a) => {
            const effectiveAmount = getEffectiveAmount(a, tenderBase);
            const pct = getPercentValue(a, tenderBase, effectiveAmount);
            const deltaAmt = Number(a.deltaAmount || 0);
            return {
              ...a,
              effectiveAmount,
              pct,
              deltaAmt,
              appliedOn: getAppliedAt(a),
            };
          })
          .sort((x, yv) => x.effectiveAmount - yv.effectiveAmount)
          .map((x, i) => ({
            sr: String(i + 1),
            vendor: toAscii(x.vendorName || x.vendorUid?.slice(0, 12) || "—"),
            city: compactCity(x.city || "—"),
            quoted: INR(x.effectiveAmount),
            pct: `${x.pct >= 0 ? "+" : "-"}${Math.abs(Number(x.pct || 0)).toFixed(2)}%`,
            delta: INR(x.deltaAmt),
            abbl: toAscii(x.selectType || "—"),
            applied: dayjs(x.appliedOn).format("DD/MM/YY HH:mm"),
            rank: i === 0 ? "L1" : i === 1 ? "L2" : i === 2 ? "L3" : String(i + 1),
          }));

        const headers = [
          "Sr",
          "Vendor",
          "City",
          "Quoted",
          "+/-%",
          "Delta",
          "AB/BL",
          "Applied",
          "Rank",
        ];

        const cols = [24, 140, 60, 65, 42, 60, 50, 70, 40];

        const tableRows = ranked.length
          ? ranked.map((r) => [
              r.sr,
              r.vendor,
              r.city,
              r.quoted,
              r.pct,
              r.delta,
              r.abbl,
              r.applied,
              r.rank,
            ])
          : [["—", "No applications received.", "", "", "", "", "", "", ""]];

        y = ensureSpace(pdf, y, 58);
        y = drawTablePaged(pdf, y, cols, headers, tableRows);

        y += 12;
        if (y > H - 60 && cardIdx < openedBlocks.length - 1) {
          pdf.addPage();
          drawHeader(pdf);
          y = sectionTitle(pdf, "OPEN TENDERS (cont.)", 96);
        }
      });
    }

    y = sectionTitle(pdf, "LOCKED TENDERS (summary)", y);

    if (!lockedBlocks.length) {
      pdf.setTextColor(...palette.sub);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      pdf.text("No locked tenders.", 32, y);
      pdf.setTextColor(...palette.text);
      y += 18;
    } else {
      lockedBlocks.forEach((b, idx) => {
        const titleMaxW = W - 64;
        const titleWrap = textLines(pdf, b.tender?.title || "Tender", titleMaxW);
        const titleH = Math.max(18, titleWrap.length * 12);
        const rowH = titleH + 34;

        y = ensureSpace(pdf, y, rowH + 12);

        pdf.setFillColor(...palette.amber);
        pdf.setDrawColor(200);
        pdf.rect(32, y, W - 64, rowH, "FD");

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11.2);
        pdf.text(titleWrap, 40, y + 16);

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9.6);
        pdf.text(
          `Company: ${b.tender?.ownerCompany || "—"}   City: ${b.tender?.city || "—"}`,
          40,
          y + 16 + titleH + 4
        );

        textRight(
          pdf,
          `Opens At: ${b.openAt ? dayjs(b.openAt).format("DD/MM/YYYY, hh:mm A") : "—"}`,
          W - 40,
          y + 16
        );
        textRight(pdf, `Total Applications: ${b.apps?.length || 0}`, W - 40, y + 16 + titleH + 4);

        y += rowH + 10;
        if (y > H - 60 && idx < lockedBlocks.length - 1) {
          pdf.addPage();
          drawHeader(pdf);
          y = sectionTitle(pdf, "LOCKED TENDERS (summary, cont.)", 96);
        }
      });
    }

    const pageCount = pdf.internal.getNumberOfPages();
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setTextColor(...palette.sub);
      textRight(pdf, `Page ${i} of ${pageCount}`, W - 32, H - 18);
    }

    pdf.save(`Tender_Results_${dayjs().format("YYYYMMDD_HHmm")}.pdf`);
  };

  useEffect(() => {
    let any = false;
    Object.keys(dataMap).forEach((tid) => {
      const { openAt } = dataMap[tid] || {};
      const opened = !openAt || Date.now() >= new Date(openAt).getTime();
      if (opened && !pdfTriggeredRef.current[tid]) {
        pdfTriggeredRef.current[tid] = true;
        any = true;
      }
    });
    if (any) {
      const id = setTimeout(downloadPDF, 500);
      return () => clearTimeout(id);
    }
  }, [tick, dataMap]);

  /* ------------------ UI ------------------ */
  if (!ready) return null;

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="bg-white border rounded-xl p-6 text-center">
          <div className="text-lg font-semibold text-emerald-700">
            Please login to view your tenders.
          </div>
          <Link
            className="text-sm text-emerald-700 underline mt-2 inline-block"
            to="/tender?tab=login"
          >
            Go to Vendor Login
          </Link>
        </div>
      </div>
    );
  }

  const tenderIds = Object.keys(dataMap).sort((a, b) => {
    const ta = dataMap[a]?.tender;
    const tb = dataMap[b]?.tender;
    return getTenderSortTime(tb) - getTenderSortTime(ta);
  });

  const leftMin = Math.max(0, Math.floor((deadline - Date.now()) / 60000));
  const leftSec = Math.max(0, Math.floor(((deadline - Date.now()) % 60000) / 1000));

  const doLogout = async () => {
    try {
      await signOut(auth);
    } catch {}
    nav("/tender?tab=login");
  };

  return (
    <div className="min-h-screen bg-emerald-50 p-6">
      <div className="max-w-7xl mx-auto bg-white rounded-2xl border shadow-sm p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-extrabold text-emerald-800">
              My Tender Results
            </h1>
            <p className="text-xs text-gray-500 mt-1">
              You will be signed out automatically in{" "}
              <b>
                {String(leftMin).padStart(2, "0")}:
                {String(leftSec).padStart(2, "0")}
              </b>
              .
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadPDF}
              className="px-3 py-1.5 rounded-lg border text-sm hover:bg-gray-50"
            >
              Download PDF
            </button>
            <button
              onClick={doLogout}
              className="px-3 py-1.5 rounded-lg border border-red-500 text-red-600 text-sm hover:bg-red-50"
            >
              Logout
            </button>
            <Link
              to="/tender/list"
              className="text-emerald-700 underline text-sm"
            >
              ← Back to Live Tenders
            </Link>
          </div>
        </div>

        {!tenderIds.length && (
          <div className="mt-6 p-4 border rounded-lg bg-gray-50 text-gray-600 text-sm">
            You have not applied to any tender yet.
          </div>
        )}

        <div className="mt-4 grid gap-6">
          {tenderIds.map((tid) => {
            const block = dataMap[tid];
            const t = block?.tender;
            const apps = block?.apps || [];
            const openAt = block?.openAt;
            const opened = !openAt || Date.now() >= new Date(openAt).getTime();
            const tenderBase = Number(t?.baseAmount || t?.amount || 0);

            const ranked = (apps || [])
              .map((a) => {
                const effectiveAmount = getEffectiveAmount(a, tenderBase);
                const pct = getPercentValue(a, tenderBase, effectiveAmount);
                const deltaAmt = Number(a.deltaAmount || 0);
                return {
                  ...a,
                  effectiveAmount,
                  pct,
                  deltaAmt,
                  appliedOn: getAppliedAt(a),
                };
              })
              .sort((x, y) => x.effectiveAmount - y.effectiveAmount)
              .map((x, i) => ({
                ...x,
                rank: i + 1,
                tag: i === 0 ? "L1" : i === 1 ? "L2" : i === 2 ? "L3" : "",
              }));

            const maskedRows = new Array(apps.length || 1)
              .fill(0)
              .map((_, i) => i + 1);

            return (
              <section key={tid} className="border rounded-xl p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-lg font-bold text-emerald-800">
                      {toAscii(t?.title || "Tender")}
                    </div>
                    <div className="text-sm text-gray-600">
                      <b>Company:</b> {toAscii(t?.ownerCompany || "—")} &nbsp;•&nbsp;{" "}
                      <b>City:</b> {toAscii(t?.city || "—")}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-500">Estimated Value</div>
                    <div className="text-base font-bold">
                      {INR(t?.baseAmount || t?.amount)}
                    </div>
                  </div>
                </div>

                <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm">
                  <div className="p-3 rounded-lg bg-gray-50 border">
                    <div className="text-gray-500">Start</div>
                    <div className="font-semibold">
                      {t?.startAt || t?.openDate
                        ? dayjs(t?.startAt || t?.openDate).format(
                            "DD/MM/YYYY, hh:mm A"
                          )
                        : "—"}
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-gray-50 border">
                    <div className="text-gray-500">Last Date</div>
                    <div className="font-semibold">
                      {t?.endAt || t?.closeDate
                        ? dayjs(t?.endAt || t?.closeDate).format(
                            "DD/MM/YYYY, hh:mm A"
                          )
                        : "—"}
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-gray-50 border">
                    <div className="text-gray-500">
                      {opened ? "Opened At" : "Opens In"}
                    </div>
                    <div
                      className={cx("font-semibold", opened && "text-emerald-700")}
                    >
                      {opened
                        ? openAt
                          ? dayjs(openAt).format("DD/MM/YYYY, hh:mm A")
                          : "—"
                        : remainingVerbose(openAt, Date.now())}
                    </div>
                  </div>
                </div>

                <div className="mt-3 text-sm">
                  Total Applications Received: <b>{apps.length}</b>
                </div>

                <div className="mt-3 relative">
                  {!opened && (
                    <div className="absolute inset-0 bg-white/75 backdrop-blur-[2px] z-10 flex flex-col items-center justify-center">
                      <div className="text-emerald-700 font-semibold">
                        Unlocks in: {remainingVerbose(openAt, Date.now())}
                      </div>
                      <div className="text-xs text-gray-600">
                        Details will unlock automatically after opening time.
                      </div>
                    </div>
                  )}

                  <div
                    className={cx(
                      "overflow-auto rounded-lg border",
                      !opened && "select-none"
                    )}
                  >
                    <table className="min-w-full text-sm">
                      <thead className="bg-emerald-50">
                        <tr className="whitespace-nowrap">
                          <th className="px-3 py-2 text-left w-12">Sr</th>
                          <th className="px-3 py-2 text-left">Vendor</th>
                          <th className="px-3 py-2">City</th>
                          <th className="px-3 py-2">Quoted</th>
                          <th className="px-3 py-2">± %</th>
                          <th className="px-3 py-2">Δ (₹)</th>
                          <th className="px-3 py-2">AB/BL</th>
                          <th className="px-3 py-2">Applied At</th>
                          <th className="px-3 py-2">Rank</th>
                        </tr>
                      </thead>
                      <tbody>
                        {opened ? (
                          (ranked.length ? ranked : [{ empty: true }]).map((r, i) =>
                            r.empty ? (
                              <tr key="empty" className="border-t">
                                <td
                                  colSpan={9}
                                  className="px-3 py-4 text-center text-gray-500"
                                >
                                  No applications yet.
                                </td>
                              </tr>
                            ) : (
                              <tr
                                key={r.id}
                                className="border-t hover:bg-emerald-50/40 whitespace-nowrap"
                              >
                                <td className="px-3 py-2">{i + 1}</td>
                                <td className="px-3 py-2 max-w-[260px] overflow-hidden text-ellipsis">
                                  {toAscii(r.vendorName || r.vendorUid?.slice(0, 8))}
                                </td>
                                <td className="px-3 py-2">
                                  {toAscii(r.city || "—")}
                                </td>
                                <td className="px-3 py-2 font-semibold">
                                  {INR(r.effectiveAmount)}
                                </td>
                                <td className="px-3 py-2">
                                  {(r.pct >= 0 ? "+" : "−")}
                                  {Math.abs(Number(r.pct || 0)).toFixed(2)}%
                                </td>
                                <td className="px-3 py-2">{INR(r.deltaAmt)}</td>
                                <td className="px-3 py-2">
                                  {toAscii(r.selectType || "—")}
                                </td>
                                <td className="px-3 py-2">
                                  {dayjs(r.appliedOn).format("DD/MM/YYYY, hh:mm A")}
                                </td>
                                <td className="px-3 py-2">
                                  <span
                                    className={cx(
                                      "px-2 py-1 rounded text-xs font-semibold",
                                      r.tag === "L1"
                                        ? "bg-green-100 text-green-700"
                                        : r.tag === "L2"
                                        ? "bg-yellow-100 text-yellow-700"
                                        : r.tag === "L3"
                                        ? "bg-blue-100 text-blue-700"
                                        : "bg-gray-100 text-gray-700"
                                    )}
                                  >
                                    {r.tag || r.rank}
                                  </span>
                                </td>
                              </tr>
                            )
                          )
                        ) : (
                          (maskedRows.length ? maskedRows : [1]).map((sr, idx) => (
                            <tr key={idx} className="border-t whitespace-nowrap">
                              <td className="px-3 py-2">{sr}</td>
                              <td className="px-3 py-2 text-gray-400">••••••••••</td>
                              <td className="px-3 py-2 text-gray-400">••••</td>
                              <td className="px-3 py-2 text-gray-400">•••••••</td>
                              <td className="px-3 py-2 text-gray-400">•••</td>
                              <td className="px-3 py-2 text-gray-400">•••••</td>
                              <td className="px-3 py-2 text-gray-400">••••</td>
                              <td className="px-3 py-2 text-gray-400">
                                ••/••/•• ••:••
                              </td>
                              <td className="px-3 py-2 text-gray-400">—</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ✅ NOTE:
   Navbar me Tender Status ka link:
   /tender?tab=login
*/